import React, { useState } from 'react';
import { Accordion, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft, faArrowRight, faUserCircle
} from '@fortawesome/free-solid-svg-icons';
import { useMsal } from '@azure/msal-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSecurity } from '../../Contexts/SecurityContext';
import { getFirstAuthorizedRoute, navigationFooterSections, navigationSections } from '../../Navigation/navigationConfig';
import LogoExpanded from '../../Assets/svg/logotipo_losa.svg';
import LogoCollapsed from '../../Assets/svg/isotipo_losa.svg';
import './Sidebar.scss';

export const sidebarTopItems = navigationSections;
export const sidebarBottomItems = navigationFooterSections;

export const isSidebarItemVisible = (item, hasPermission, hasAnyPermission) => {
    if (item.resourceCode) return hasPermission(item.resourceCode);
    if (item.anyOf) return hasAnyPermission(item.anyOf);
    return true;
};

export const getVisibleSidebarItems = (items, hasPermission, hasAnyPermission) => (
    items.reduce((visible, item) => {
        if (!item.children) {
            if (isSidebarItemVisible(item, hasPermission, hasAnyPermission)) visible.push(item);
            return visible;
        }

        const children = item.children.filter(child => (
            isSidebarItemVisible(child, hasPermission, hasAnyPermission)
        ));
        if (children.length > 0) visible.push({ ...item, children });
        return visible;
    }, [])
);

const Sidebar = () => {
    const [expanded, setExpanded] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { instance } = useMsal();
    const { appUser, hasPermission, hasAnyPermission } = useSecurity();
    const activeAccount = instance.getActiveAccount();

    const renderItem = item => {
        const children = item.children || [];

        if (item.children) {
            const active = children.some(child => location.pathname.startsWith(child.route));
            return (
                <Accordion.Item eventKey={item.eventKey} key={item.eventKey}>
                    <Accordion.Header onClick={() => setExpanded(true)}>
                        <div className={`sidebar-item ${active ? 'active' : ''}`}>
                            <FontAwesomeIcon icon={item.icon} />
                            {expanded && <span>{item.label}</span>}
                        </div>
                    </Accordion.Header>
                    <Accordion.Body>
                        {children.map(child => (
                            <div
                                className={`submenu-item ${location.pathname.startsWith(child.route) ? 'active' : ''}`}
                                onClick={() => navigate(child.route)}
                                key={`${item.eventKey}-${child.route}`}
                            >
                                {child.label}
                            </div>
                        ))}
                    </Accordion.Body>
                </Accordion.Item>
            );
        }

        return (
            <div
                className={`sidebar-item ${location.pathname.startsWith(item.route) ? 'active' : ''}`}
                onClick={() => navigate(item.isHome ? (getFirstAuthorizedRoute({ hasPermission }) || '/home') : item.route)}
                key={item.route}
            >
                <FontAwesomeIcon icon={item.icon} />
                {expanded && <span>{item.label}</span>}
            </div>
        );
    };

    return (
        <div className={`sidebar ${expanded ? 'expanded' : ''}`}>
            <div className="sidebar-item brand" onClick={() => setExpanded(value => !value)}>
                {expanded
                    ? <img src={LogoExpanded} alt="Logo" className="brand-expanded" />
                    : <img src={LogoCollapsed} alt="Logo" className="brand-collapsed" />}
            </div>
            <div className={`user-item ${expanded ? 'expanded' : ''}`}>
                <FontAwesomeIcon icon={faUserCircle} className="text-primary" />
                {expanded && (
                    <div className="user-info">
                        <div className="user-info-text d-flex flex-column align-items-start">
                            <p className="fw-bold m-0">{appUser?.displayName || activeAccount?.name}</p>
                            <p className="small fw-normal text-secondary-emphasis m-0">{activeAccount?.username?.split('@')[0]}</p>
                        </div>
                        <Dropdown align="end">
                            <Dropdown.Toggle variant="transparent" id="dropdown-profile" />
                            <Dropdown.Menu>
                                <Dropdown.Item as="button" onClick={() => instance.logoutRedirect()}>Sign out</Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                )}
            </div>
            <div className="action-items">
                <Accordion flush alwaysOpen className="d-flex flex-column justify-content-between h-100">
                    <div className="top-items">{getVisibleSidebarItems(sidebarTopItems, hasPermission, hasAnyPermission).map(renderItem)}</div>
                    <div className="bottom-items">{getVisibleSidebarItems(sidebarBottomItems, hasPermission, hasAnyPermission).map(renderItem)}</div>
                </Accordion>
            </div>
            <div className="sidebar-item" onClick={() => setExpanded(value => !value)}>
                <FontAwesomeIcon icon={expanded ? faArrowLeft : faArrowRight} />
                {expanded && <span>Collapse</span>}
            </div>
        </div>
    );
};

export default Sidebar;
